import { isDefined } from "@clipboard-health/util-ts";
import { useSession } from "@src/appV2/Auth/api/useSession";
import {
  formatRushFeeAmount,
  type LegacyRushFee,
  narrowRushFeeConfig,
  resolveRushFee,
  type RushFeeInfo,
  type RushFeeTier,
} from "@src/appV2/redesign/utils/rushFee";
import { useTierRushFeeSchedule } from "@src/appV2/redesign/utils/useTierRushFeeSchedule";
import { formatChargeDifferentialPercent } from "@src/appV2/redesign/WorkplaceChargeLeadTimeDifferentials/constants";
import { useMemo } from "react";

interface UseRushFeeCheckOptions {
  startTime: Date | undefined;
}

interface UseRushFeeCheckReturn {
  hasRushFee: boolean;
  /** UI-shaped, pre-formatted view of the applicable rush fee. */
  rushFeeInfo: RushFeeInfo | undefined;
  /** Sorted ascending by threshold. Used by the bulk popup to render the rate card. */
  tierSchedule: RushFeeTier[];
  rushFeeConfig: LegacyRushFee | undefined;
}

/**
 * Resolves whether a rush fee applies for `startTime`. Priority: tier schedule
 * when configured, otherwise the legacy `profile.rushFee` window. See
 * `useTierRushFeeSchedule` for the flag-gating and fetch-state semantics.
 */
export function useRushFeeCheck({ startTime }: UseRushFeeCheckOptions): UseRushFeeCheckReturn {
  const { profile } = useSession();
  const rushFeeConfig = narrowRushFeeConfig(profile.rushFee);
  const workplaceId = profile.userId;

  const { tierSchedule, skipRushFeeChecks } = useTierRushFeeSchedule({ workplaceId });

  const rushFeeInfo = useMemo<RushFeeInfo | undefined>(() => {
    if (skipRushFeeChecks || !startTime) {
      return undefined;
    }
    const resolution = resolveRushFee(tierSchedule, rushFeeConfig, startTime);
    if (!resolution) {
      return undefined;
    }
    if (resolution.source === "tier") {
      return {
        amount: formatChargeDifferentialPercent(resolution.applicableTier.incrementalMultiplier),
        hoursUntilShift: resolution.applicableTier.thresholdInHours,
      };
    }
    if (!isDefined(rushFeeConfig)) {
      return undefined;
    }
    return {
      amount: formatRushFeeAmount(rushFeeConfig.differential),
      hoursUntilShift: rushFeeConfig.period,
    };
  }, [skipRushFeeChecks, startTime, tierSchedule, rushFeeConfig]);

  return {
    hasRushFee: isDefined(rushFeeInfo),
    rushFeeInfo,
    tierSchedule,
    rushFeeConfig,
  };
}
